import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'
import { useStore } from '../../store'
import Script from 'next/script'
import { colors, ELanguage, formatCanonicalUrl } from '@microfrontends/react-components'
import { getPathFromUrl } from '../../helpers/common'
import translations from './translations'
import { TMetaHead } from '../../helpers/urlHelper'

const OG_IMAGE = `${process.env.NEXT_PUBLIC_IMAGE_URL}/images/vnw_empower_growth_logo.png?v=${process.env.BUILD_ID}`
const JS_BASE_URL = process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL
const VNW_LOGO = `${JS_BASE_URL}/assets-page-container/images/vnw_empower_growth_logo.png`

export type TPageMetaTagsProps = {
    title?: string
    description?: string
    keywords?: string
    ogImageUrl?: string
    language?: ELanguage
    pageName: string
    metaHead?: TMetaHead
}

const MetaTags = (props: TPageMetaTagsProps) => {
    const {
        description = 'Search for jobs hiring using our Job Search. Thousands of jobs in different field in Vietnam. Find your dream jobs and apply with just 1 click now!',
        ogImageUrl = OG_IMAGE,
        pageName = '',
        metaHead,
        language
    } = props
    const { t, langCode } = useStore().state
    const router = useRouter()

    const translate = (key: string) => {
        return translations[key] || key
    }

    let pathSplit = router.asPath.split('/')
    for (let idx = 0; idx < pathSplit.length; idx++) {
        pathSplit[idx] = translate(pathSplit[idx])
    }

    const currentUrl = getPathFromUrl(`${JS_BASE_URL}${pathSplit.join('/')}`)

    const favicon = `${JS_BASE_URL}/favicon.ico?v=${process.env.BUILD_ID}`
    const titleText: string = metaHead?.title || `${t('meta_title')} | VietnamWorks`
    const descriptionText: string = metaHead?.description || t('meta_description')
    const canonicalUrl = metaHead?.canonicalUrl || formatCanonicalUrl(currentUrl)
    const twitterImageUID = Date.now().toString(36)

    let robotsContent = metaHead?.robots || 'index, follow'
    if (router.asPath.indexOf('?') > 0) {
        robotsContent = 'noindex, nofollow'
    }
    if (language === ELanguage.EN) {
        robotsContent = 'noindex, nofollow'
    }

    return (
        <>
            <Head>
                <title key='title'>{titleText}</title>
                <meta charSet='utf-8' />
                <meta name='viewport' content='initial-scale=1.0, width=device-width' />
                <meta httpEquiv='X-UA-Compatible' content='IE=edge' />
                <link rel='shortcut icon' href={favicon} type='image/x-icon' />
                <meta key='description' name='description' content={descriptionText} />
                <meta name='theme-color' content={colors.primary[20]} />
                <meta name='robots' content={robotsContent} />
                <meta property='og:url' content={currentUrl} />
                <meta key='ogTitle' property='og:title' content={titleText} />
                <meta key='ogDescription' property='og:description' content={descriptionText} />
                <meta name='twitter:site' content={currentUrl} />
                <meta key='twitterTitle' name='twitter:title' content={titleText} />
                <meta key='twitterDescription' name='twitter:description' content={descriptionText} />
                <meta name='twitter:image' content={ogImageUrl + `?${twitterImageUID}`} />
                <meta name='twitter:image:src' content={ogImageUrl + `?${twitterImageUID}`} />
                <meta name='twitter:card' content='summary_large_image' />
                <meta property='og:image' content={ogImageUrl} />
                <meta property='og:image:secure_url' content={ogImageUrl} />
                <meta property='og:image:width' content='526' />
                <meta property='og:image:height' content='275' />
                <link rel='canonical' href={canonicalUrl} />
                <link rel='preconnect' href='https://google.com.vn' />
                <link rel='preconnect' href='https://googleads.g.doubleclick.net' />
                <link rel='preconnect' href='https://www.googletagmanager.com' />
                <link rel='preconnect' href='https://www.google-analytics.com' />
                <link rel='preconnect' href='https://stats.g.doubleclick.net' />
                <link rel='preconnect' href='https://www.google.com' />
                <link rel='preconnect' href='https://connect.facebook.net' />
                <link rel='preconnect' href='https://www.facebook.com' />
                <link rel='preconnect' href='https://www.googleadservices.com' />
                <link rel='preconnect' href='https://images.vietnamworks.com' />
                <link rel='preload' as='image' href={VNW_LOGO} />
            </Head>

            <Script
                id='ga'
                dangerouslySetInnerHTML={{
                    __html: `
                    var _gaq = _gaq || [];
                    var pluginUrl = '//www.google-analytics.com/plugins/ga/inpage_linkid.js';
                    _gaq.push(['_require', 'inpage_linkid', pluginUrl]);
                    _gaq.push(['_setAccount', '${process.env.NEXT_PUBLIC_GA_ACCOUNT}']);
                    _gaq.push(['_setDomainName', '${process.env.NEXT_PUBLIC_GA_DOMAIN}']);
                    _gaq.push(['_trackPageview']);

                    (function() {
                        var ga = document.createElement('script'); ga.type = 'text/javascript'; ga.async = true;
                        ga.src = ('https:' == document.location.protocol ? 'https://' : 'http://') + 'stats.g.doubleclick.net/dc.js';
                        var s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(ga, s);
                    })();
                    function customEvent (uniqueName, action, label, value) {
                        if (_gaq) {
                            _gaq.push(['_trackEvent', uniqueName, action, label], value || 1); // 'Videos', 'Play', 'First Birthday'
                        }
                    }`
                }}
            />
            <Script
                id='gtm'
                dangerouslySetInnerHTML={{
                    __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID}');`
                }}
            />
            <Script async src={`${process.env.NEXT_PUBLIC_APP_ASSET_URL}assets-page-container/js/jquery.min.js`} />
        </>
    )
}

export default MetaTags
